import type { Product, ProductMedia, ProductOptionGroup, ProductVariant, SiteSettings } from "./types";

const productImage = (id: string, filename: string, alt: string): ProductMedia => ({
  id,
  kind: "image",
  url: `/media/products/${filename}`,
  alt
});

const productVideo = (id: string, filename: string, poster: string, alt: string): ProductMedia => ({
  id,
  kind: "video",
  url: `/media/videos/${filename}`,
  poster: `/media/products/${poster}`,
  alt
});

const guide = (id: string, filename: string, alt: string): ProductMedia => ({
  id,
  kind: "image",
  url: `/media/guides/${filename}`,
  alt
});

const values = (items: Array<[string, string]>) => items.map(([id, label]) => ({ id, label }));

const sizesLarge = values([
  ["70x70", "70cm × 70cm"],
  ["70x100", "70cm × 100cm"],
  ["70x150", "70cm × 150cm"],
  ["70x200", "70cm × 200cm"]
]);

const hanging = values([
  ["loop", "Loop"],
  ["gold-eyelet", "Gold eyelet"]
]);

const lace = values([
  ["lace-1", "Lace style 1"],
  ["lace-2", "Lace style 2"],
  ["lace-3", "Lace style 3"],
  ["lace-4", "Lace style 4"]
]);

function twoOptionVariants(
  prefix: string,
  firstKey: string,
  first: Array<{ id: string; label: string }>,
  secondKey: string,
  second: Array<{ id: string; label: string }>,
  prices: Record<string, number | Record<string, number>>
): ProductVariant[] {
  const result: ProductVariant[] = [];
  for (const firstValue of first) {
    for (const secondValue of second) {
      const priceSource = prices[firstValue.id];
      const priceCents = typeof priceSource === "number" ? priceSource : priceSource[secondValue.id];
      result.push({
        id: `${prefix}-${firstValue.id}-${secondValue.id}`,
        label: `${firstValue.label} / ${secondValue.label}`,
        priceCents,
        available: true,
        selections: { [firstKey]: firstValue.id, [secondKey]: secondValue.id }
      });
    }
  }
  return result;
}

const sizeOption = (id: string, availableValues = sizesLarge): ProductOptionGroup => ({
  id,
  label: "Size",
  required: true,
  values: availableValues
});

const commonDescription = "Handmade to order in Australia using carefully selected fabric and premium vinyl lettering. Choose your options and enter the wording exactly as it should appear. For another size or a different concept, send a custom enquiry before ordering.";

export const fallbackCatalog: Product[] = [
  {
    id: "linen-sign",
    slug: "custom-linen-fabric-sign",
    sku: "PSS-LINEN",
    name: "Custom Linen Fabric Sign",
    shortDescription: "Personalised linen fabric signage with your choice of size, hanging finish and design.",
    description: commonDescription,
    material: "Linen",
    category: "Fabric Signs",
    active: true,
    featured: true,
    position: 1,
    etsyUrl: "https://www.etsy.com/au/listing/4352527950/custom-linen-fabric-sign-vinyl-lettering",
    personalisationMode: "single",
    styleSelector: "design",
    personalisationPrompt: "Choose a design number and enter the names, date and wording exactly as they should appear.",
    guideSet: "linen",
    options: [
      sizeOption("size"),
      { id: "hanging", label: "Hanging finish", required: true, values: hanging }
    ],
    variants: twoOptionVariants("linen", "size", sizesLarge, "hanging", hanging, {
      "70x70": 8900,
      "70x100": 12000,
      "70x150": 15000,
      "70x200": 18000
    }),
    media: [
      productImage("linen-main", "pressed-studio-linen-hanging-sign-01-full.jpg", "Personalised linen hanging sign displayed outdoors"),
      productVideo("linen-video", "pressed-studio-linen-product.mp4", "pressed-studio-linen-hanging-sign-01-full.jpg", "A linen sign moving gently in natural light")
    ],
    guides: [
      guide("size-guide", "pressed-studio-size-guide.jpg", "Fabric sign size guide"),
      guide("layouts-one", "pressed-studio-layout-options-01-04.jpg", "Design layouts 1 to 4"),
      guide("layouts-two", "pressed-studio-layout-options-05-08.jpg", "Design layouts 5 to 8"),
      guide("hanging-guide", "pressed-studio-hanging-options.jpg", "Loop and gold eyelet hanging finishes")
    ],
    seoTitle: "Custom Linen Fabric Sign | Pressed Studio Signs",
    seoDescription: "Personalised linen fabric signage, handmade to order in Australia."
  },
  {
    id: "chiffon-hanging",
    slug: "chiffon-lace-hanging-sign",
    sku: "PSS-CHIFFON-HANG",
    name: "Chiffon and Lace Hanging Sign",
    shortDescription: "Personalised chiffon signage with a delicate lace finish and your chosen hanging style.",
    description: commonDescription,
    material: "Chiffon + lace",
    category: "Fabric Signs",
    active: true,
    featured: true,
    position: 2,
    etsyUrl: "https://www.etsy.com/au/listing/4411416392/custom-chiffon-and-lace-fabric-sign",
    personalisationMode: "single",
    styleSelector: "design",
    personalisationPrompt: "Choose a design number and enter the wording exactly as it should appear.",
    guideSet: "chiffon-hanging",
    options: [
      sizeOption("size"),
      { id: "hanging", label: "Hanging finish", required: true, values: hanging }
    ],
    variants: twoOptionVariants("chiffon-hanging", "size", sizesLarge, "hanging", hanging, {
      "70x70": 10000,
      "70x100": 12000,
      "70x150": 15000,
      "70x200": 18000
    }),
    media: [
      productImage("chiffon-hanging-main", "pressed-studio-chiffon-hanging-sign-01-full.jpg", "Personalised chiffon and lace hanging sign outdoors"),
      productImage("chiffon-hanging-garden", "pressed-studio-chiffon-garden-full.jpg", "Chiffon and lace sign displayed among white flowers"),
      productVideo("chiffon-hanging-video", "pressed-studio-chiffon-product.mp4", "pressed-studio-chiffon-hanging-sign-01-full.jpg", "Chiffon fabric sign moving in natural light")
    ],
    guides: [
      guide("size-guide", "pressed-studio-size-guide.jpg", "Fabric sign size guide"),
      guide("layouts-one", "pressed-studio-layout-options-01-04.jpg", "Design layouts 1 to 4"),
      guide("layouts-two", "pressed-studio-layout-options-05-08.jpg", "Design layouts 5 to 8"),
      guide("hanging-guide", "pressed-studio-hanging-options.jpg", "Loop and gold eyelet hanging finishes")
    ],
    seoTitle: "Chiffon and Lace Hanging Sign | Pressed Studio Signs",
    seoDescription: "Personalised chiffon and lace hanging signage, handmade in Australia."
  },
  {
    id: "chiffon-lace-sign",
    slug: "custom-chiffon-lace-fabric-sign",
    sku: "PSS-CHIFFON-LACE",
    name: "Custom Chiffon and Lace Fabric Sign",
    shortDescription: "Personalised chiffon fabric signage finished with your choice of lace.",
    description: commonDescription,
    material: "Chiffon + lace",
    category: "Fabric Signs",
    active: true,
    featured: true,
    position: 3,
    etsyUrl: "https://www.etsy.com/au/listing/4437562048/custom-chiffon-and-lace-fabric-sign",
    personalisationMode: "single",
    styleSelector: "design",
    personalisationPrompt: "Choose a design number and enter the wording exactly as it should appear.",
    guideSet: "chiffon-lace",
    options: [
      sizeOption("size", values([
        ["70x70", "70cm × 70cm"],
        ["70x100", "70cm × 100cm"],
        ["70x150", "70cm × 150cm"],
        ["70x200", "70cm × 200cm"],
        ["90x90", "90cm × 90cm"],
        ["90x150", "90cm × 150cm"],
        ["90x200", "90cm × 200cm"]
      ])),
      { id: "lace", label: "Lace style", required: true, values: lace }
    ],
    variants: twoOptionVariants(
      "chiffon-lace",
      "size",
      values([
        ["70x70", "70cm × 70cm"],
        ["70x100", "70cm × 100cm"],
        ["70x150", "70cm × 150cm"],
        ["70x200", "70cm × 200cm"],
        ["90x90", "90cm × 90cm"],
        ["90x150", "90cm × 150cm"],
        ["90x200", "90cm × 200cm"]
      ]),
      "lace",
      lace,
      {
        "70x70": { "lace-1": 10000, "lace-2": 11500, "lace-3": 10000, "lace-4": 10500 },
        "70x100": { "lace-1": 12000, "lace-2": 14000, "lace-3": 12000, "lace-4": 12500 },
        "70x150": { "lace-1": 15000, "lace-2": 17500, "lace-3": 15000, "lace-4": 16000 },
        "70x200": { "lace-1": 18000, "lace-2": 20000, "lace-3": 18000, "lace-4": 18500 },
        "90x90": { "lace-1": 12000, "lace-2": 14000, "lace-3": 12000, "lace-4": 12500 },
        "90x150": { "lace-1": 19000, "lace-2": 22000, "lace-3": 19000, "lace-4": 20000 },
        "90x200": { "lace-1": 20000, "lace-2": 23000, "lace-3": 20000, "lace-4": 22000 }
      }
    ),
    media: [
      productImage("chiffon-lace-main", "pressed-studio-chiffon-garden-full.jpg", "Personalised chiffon and lace sign displayed with white flowers"),
      productImage("chiffon-lace-detail", "pressed-studio-chiffon-garden-detail.jpg", "Detail of lettering and lace on chiffon"),
      productImage("chiffon-lace-small", "pressed-studio-chiffon-small-sign-01-full.jpg", "Small personalised chiffon and lace fabric sign"),
      productVideo("chiffon-lace-video", "pressed-studio-chiffon-product.mp4", "pressed-studio-chiffon-garden-full.jpg", "Chiffon and lace fabric sign in motion")
    ],
    guides: [
      guide("size-guide", "pressed-studio-size-guide.jpg", "Fabric sign size guide"),
      guide("layouts-one", "pressed-studio-layout-options-01-04.jpg", "Design layouts 1 to 4"),
      guide("layouts-two", "pressed-studio-layout-options-05-08.jpg", "Design layouts 5 to 8"),
      guide("lace-guide", "pressed-studio-lace-options.jpg", "Lace styles 1 to 4")
    ],
    seoTitle: "Custom Chiffon and Lace Sign | Pressed Studio Signs",
    seoDescription: "Custom chiffon fabric signage with a choice of lace finishes."
  },
  {
    id: "lace-chair-signs",
    slug: "chiffon-lace-chair-signs",
    sku: "PSS-LACE-CHAIR",
    name: "Chiffon and Lace Chair Signs",
    shortDescription: "A personalised pair of chiffon chair signs with your choice of size, lace and lettering.",
    description: commonDescription,
    material: "Chiffon + lace",
    category: "Chair Signs",
    active: true,
    featured: true,
    position: 4,
    etsyUrl: "https://www.etsy.com/au/listing/4481227443/chiffon-and-lace-chair-signbanner-just",
    personalisationMode: "pair",
    styleSelector: "lettering",
    personalisationPrompt: "Enter the wording for both signs and choose lettering style 1 to 7.",
    guideSet: "lace-chair",
    options: [
      sizeOption("size", values([["30x20", "30cm × 20cm"], ["40x30", "40cm × 30cm"]])),
      { id: "lace", label: "Lace style", required: true, values: lace }
    ],
    variants: twoOptionVariants(
      "lace-chair",
      "size",
      values([["30x20", "30cm × 20cm"], ["40x30", "40cm × 30cm"]]),
      "lace",
      lace,
      {
        "30x20": { "lace-1": 4000, "lace-2": 5000, "lace-3": 4000, "lace-4": 4500 },
        "40x30": { "lace-1": 5000, "lace-2": 6000, "lace-3": 5000, "lace-4": 5500 }
      }
    ),
    media: [
      productImage("lace-chair-main", "pressed-studio-lace-chair-signs-01-full.jpg", "Just Married chiffon and lace chair signs"),
      productImage("lace-chair-detail", "pressed-studio-lace-chair-signs-02-detail.jpg", "Chiffon chair sign lettering and lace detail"),
      productImage("lace-chair-styled", "pressed-studio-lace-chair-signs-03-styled.jpg", "Chiffon chair signs at a wedding table"),
      productVideo("lace-chair-video", "pressed-studio-lace-chair-product.mp4", "pressed-studio-lace-chair-signs-01-full.jpg", "A pair of chiffon and lace chair signs")
    ],
    guides: [
      guide("lace-guide", "pressed-studio-lace-options.jpg", "Lace styles 1 to 4"),
      guide("lettering-guide", "pressed-studio-chair-lettering-options.jpg", "Lettering styles 1 to 7")
    ],
    seoTitle: "Chiffon and Lace Chair Signs | Pressed Studio Signs",
    seoDescription: "Personalised chiffon and lace chair signs for weddings and celebrations."
  },
  {
    id: "satin-chair-signs",
    slug: "satin-chair-signs",
    sku: "PSS-SATIN-CHAIR",
    name: "Satin Chair Signs",
    shortDescription: "A personalised pair of satin chair signs with your choice of size, colour and lettering.",
    description: commonDescription,
    material: "Satin",
    category: "Chair Signs",
    active: true,
    featured: true,
    position: 5,
    etsyUrl: "https://www.etsy.com/au/listing/4489980423/satin-chair-signbanner-just-married-mr",
    personalisationMode: "pair",
    styleSelector: "lettering",
    personalisationPrompt: "Enter the wording for both signs and choose lettering style 1 to 7.",
    guideSet: "satin-chair",
    options: [
      sizeOption("size", values([["30x20", "30cm × 20cm"], ["40x30", "40cm × 30cm"], ["40x50", "40cm × 50cm"]])),
      { id: "colour", label: "Satin colour", required: true, values: values([["white", "White"], ["ivory", "Ivory"]]) }
    ],
    variants: twoOptionVariants(
      "satin-chair",
      "size",
      values([["30x20", "30cm × 20cm"], ["40x30", "40cm × 30cm"], ["40x50", "40cm × 50cm"]]),
      "colour",
      values([["white", "White"], ["ivory", "Ivory"]]),
      { "30x20": 3000, "40x30": 3500, "40x50": 4000 }
    ),
    media: [
      productImage("satin-chair-main", "pressed-studio-satin-chair-signs-01-full.jpg", "Personalised satin Just Married chair signs"),
      productImage("satin-chair-detail", "pressed-studio-satin-chair-signs-02-detail.jpg", "Satin chair sign lettering detail")
    ],
    guides: [
      guide("lettering-guide", "pressed-studio-chair-lettering-options.jpg", "Lettering styles 1 to 7")
    ],
    seoTitle: "Satin Chair Signs | Pressed Studio Signs",
    seoDescription: "Personalised satin chair signs, handmade to order in Australia."
  }
];

export const fallbackSettings: SiteSettings = {
  checkoutEnabled: false,
  shippingLabel: "Australia-wide delivery",
  shippingRateCents: null,
  turnaroundMessage: "Turnaround is confirmed before production. If your date is close, contact the studio before ordering.",
  announcement: "Custom orders welcome",
  contactEmail: "",
  instagramUrl: "https://www.instagram.com/pressedstudiosigns/",
  returnsPolicy: "Add the approved returns policy before launch.",
  shippingPolicy: "Add the approved shipping rates and delivery policy before launch.",
  privacyPolicy: "Add the approved privacy policy before launch.",
  termsPolicy: "Add the approved terms before launch."
};
